import { createSelector, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { utils } from '../utils';
import { getAuth, signOut } from 'firebase/auth';
import Tenant from './tenantModel';

// SELECTORS-------------------------------------------

const selectedTenantId = state => state.tenants.selectedTenantId;

const allTenants = createSelector(
    state => state.tenants.list,
    tenants => tenants?.map(tenant => new Tenant(tenant)), 
);

const allUsers = createSelector(
    state => state.tenants.users,
    users => users,
);

const allInvites = createSelector(
    state => state.tenants.invites,
    invites => invites,
);

const usersByTenant = createSelector(
    state => state.tenants.selectedTenantUsers,
    (users) => users?.length ? users : [],
);

const invitesByTenant = createSelector(
    state => state.tenants.selectedTenantInvites,
    (invites) => invites?.length ? invites : [],
);

const usersAndInvitesByTenant = createSelector(
    usersByTenant,
    invitesByTenant,
    (users, invites) => {
        const mappedUsers = users.map(user => ({ ...user, status: 'Confirmed' }));
        const mappedInvites = invites.map(invite => ({ ...invite, status: 'Invited' }));
        return [...mappedUsers, ...mappedInvites];
    },
);

const tenantsLoading = createSelector(
    state => state.tenants.isLoading,
    isLoading => Object.values(isLoading).some(val => val),
);

const selectedTenant = createSelector(
    allTenants,
    state => state.tenants.selectedTenantId,
    (tenants, tenantId) => tenants.find(tenant => tenant.id === tenantId),
);

const selectedUser = createSelector(
    state => state.tenants.selectedUserDetail,
    (user) => user, 
);

const selectedInviteId = createSelector(
    state => state.tenants.selectedInviteId,
    inviteId => inviteId,
);

const isTenantStatusDialogOpen = createSelector(
    state => state.tenants.isTenantStatusDialogOpen,
    isOpen => isOpen,
);

const isDecommissionTenantDialogOpen = createSelector(
    state => state.tenants.isDecommissionTenantDialogOpen,
    isOpen => isOpen,
);

const isUserStatusDialogOpen = createSelector(
    state => state.tenants.isUserStatusDialogOpen,
    isOpen => isOpen,
);

const selectedTenantRoles = createSelector(
    state => state.tenants.rolesByTenantId,
    selectedTenantId,
    (roles, tenantId) => roles && tenantId ? roles[tenantId] : [],
);

const selectedTenantDealers = createSelector(
    state => state.tenants.dealersByTenantId,
    selectedTenantId,
    (dealers, tenantId) => dealers && tenantId ? dealers[tenantId] : [],
);

const selectedTenantFacilities = createSelector(
    state => state.tenants.facilitiesByTenantId,
    selectedTenantId,
    (facilities, tenantId) => facilities && tenantId ? facilities[tenantId] : [],
);

const rolesByTenantId = createSelector(
    state => state.tenants.rolesByTenantId,
    roles => roles,
);

const dealersByTenantId = createSelector(
    state => state.tenants.dealersByTenantId,
    dealers => dealers,
);

const facilitiesByTenantId = createSelector(
    state => state.tenants.facilitiesByTenantId,
    facilities => facilities,
);

const tenantAlert = createSelector(
    state => state.tenants.alert,
    alert => alert,
);

const selectedTenantUser = createSelector(
    state => state.tenants.selectedUserDetail,
    user => user,
);

const isEmailMfaEnabled = createSelector(
    state => state.tenants.isEmailMfaEnabled,
    isEnabled => isEnabled,
);

const isSamlSsoEnabled = createSelector(
    state => state.tenants.isSamlSsoEnabled,
    isEnabled => isEnabled,
);

export const tenantSelectors = {
    allTenants,
    allUsers,
    allInvites,
    usersByTenant,
    invitesByTenant,
    tenantsLoading,
    selectedTenant,
    selectedUser,
    selectedInviteId,
    rolesByTenantId,
    dealersByTenantId,
    facilitiesByTenantId,
    isTenantStatusDialogOpen,
    isDecommissionTenantDialogOpen,
    isUserStatusDialogOpen,
    tenantAlert,
    selectedTenantRoles,
    selectedTenantDealers,
    selectedTenantFacilities,
    selectedTenantUser,
    usersAndInvitesByTenant,
    isEmailMfaEnabled,
    isSamlSsoEnabled,
};

// ACTIONS -----------------------------------

const addTenant = createAsyncThunk(
    'tenants/addTenant',
    async (newTenant) => {
        const addTenantFunction = httpsCallable(getFunctions(), 'tenants-addTenant_v2');
        return await addTenantFunction(newTenant);
    },
);

const getTenants = createAsyncThunk(
    'tenants/getTenants',
    async () => {
        // Use getTenantsForUser_v2 which returns scoped tenants based on user's access
        const getTenantsFunction = httpsCallable(getFunctions(), 'tenants-getTenantsForUser_v2');
        return await getTenantsFunction();
    },
);

const updateTenant = createAsyncThunk(
    'tenants/updateTenant',
    async (updatedTenant) => {
        const updateTenantFunction = httpsCallable(getFunctions(), 'tenants-updateTenant_v2');
        return await updateTenantFunction(updatedTenant);
    },
);

const disableTenant = createAsyncThunk(
    'tenants/disableTenant',
    async (tenantId) => {
        const disableTenantFunction = httpsCallable(getFunctions(), 'tenants-disableTenant_v2');
        return await disableTenantFunction(tenantId);
    },
);

const enableTenant = createAsyncThunk(
    'tenants/enableTenant',
    async (tenantId) => {
        const enableTenantFunction = httpsCallable(getFunctions(), 'tenants-enableTenant_v2');
        return await enableTenantFunction(tenantId);
    },
);

const decommissionTenant = createAsyncThunk(
    'tenants/decommissionTenant',
    async (tenantId) => {
        const decommissionTenantFunction = httpsCallable(getFunctions(), 'tenants-decommissionTenant_v2');
        return await decommissionTenantFunction(tenantId);
    },
);

const updateUser = createAsyncThunk(
    'tenants/updateUser',
    async (updatedUser) => {
        const updateUserFunction = httpsCallable(getFunctions(), 'users-updateUserMultiTenant_v2');
        return await updateUserFunction(updatedUser);
    },
);

const disableUser = createAsyncThunk(
    'tenants/disableUser',
    async (userId) => {
        const functions = getFunctions();
        const disableUserFunction = httpsCallable(functions, 'users-disableUser_v2');
        return await disableUserFunction(userId);
    },
);

const enableUser = createAsyncThunk(
    'tenants/enableUser',
    async (userId) => {
        const functions = getFunctions();
        const enableUserFunction = httpsCallable(functions, 'users-enableUser_v2');
        return await enableUserFunction(userId);
    },
);

const getTenantRoleData = createAsyncThunk(
    'tenants/getTenantRoleData',
    async (tenantId) => {
        const getTenantRoleDataFunction = httpsCallable(getFunctions(), 'tenants-getTenantRoleData_v2');
        return await getTenantRoleDataFunction(tenantId);
    },
);

const getUsers = createAsyncThunk(
    'tenants/getUsers',
    async () => {
        const getUsersFunction = httpsCallable(getFunctions(), 'users-getUsers_v2');
        return await getUsersFunction();
    },
);

const getInvites = createAsyncThunk(
    'tenants/getInvites',
    async () => {
        const getInvitesFunction = httpsCallable(getFunctions(), 'users-getInvites_v2');
        return await getInvitesFunction();
    },
);

const getUsersByTenantId = createAsyncThunk(
    'tenants/getUsersByTenantId',
    async (tenantId) => {
        const getUsersByTenantIdFunction = httpsCallable(getFunctions(), 'users-getUsersByTenantId_v2');
        return await getUsersByTenantIdFunction(tenantId);
    },
);

const getInvitesByTenantId = createAsyncThunk(
    'tenants/getInvitesByTenantId',
    async (tenantId) => {
        const getInvitesByTenantIdFunction = httpsCallable(getFunctions(), 'users-getInvitesByTenantId_v2');
        return await getInvitesByTenantIdFunction(tenantId);
    },
);

const inviteUser = createAsyncThunk(
    'tenants/inviteUser',
    async (newUser) => {
        const functions = getFunctions();
        const inviteUserFunction = httpsCallable(functions, 'users-inviteUser_v2');
        return await inviteUserFunction(newUser);
    },
);

const getUserDetail = createAsyncThunk(
    'tenants/getUserDetail',
    async (userId) => {
        const getUserDetail = httpsCallable(getFunctions(), 'users-getUserDetailById_v2');
        return await getUserDetail(userId);
    },
);

const resetMFAForUser = createAsyncThunk(
    'tenants/resetMFAEnrollment',
    async ({ userId, tenantId }, { getState }) => {
        const resolvedTenantId = tenantId ?? getState()?.auth?.currentSelectedTenant;
        const functions = getFunctions();
        const resetMFAForUserFunction = httpsCallable(functions, 'auth-resetMFAEnrollment_v2');
        return await resetMFAForUserFunction({ userId, tenantId: resolvedTenantId });
    },
);

const resetPasskeyForUser = createAsyncThunk(
    'tenants/resetPasskeyForUser',
    async ({ userId, tenantId }, { getState }) => {
        const resolvedTenantId = tenantId ?? getState()?.auth?.currentSelectedTenant;
        const functions = getFunctions();
        const resetPasskeyForUserFunction = httpsCallable(functions, 'auth-resetPasskeyForUser_v2');
        return await resetPasskeyForUserFunction({ userId, tenantId: resolvedTenantId });
    },
);

const resetTOTPForUser = createAsyncThunk(
    'tenants/resetTOTPForUser',
    async ({ userId, tenantId }, { getState }) => {
        const resolvedTenantId = tenantId ?? getState()?.auth?.currentSelectedTenant;
        const functions = getFunctions();
        const resetTOTPForUserFunction = httpsCallable(functions, 'auth-resetTOTPForUser_v2');
        return await resetTOTPForUserFunction({ userId, tenantId: resolvedTenantId });
    },
);

const resetEmailMFAForUser = createAsyncThunk(
    'tenants/resetEmailMFAForUser',
    async ({ userId, tenantId }, { getState }) => {
        const resolvedTenantId = tenantId ?? getState()?.auth?.currentSelectedTenant;
        const functions = getFunctions();
        const resetEmailMFAForUserFunction = httpsCallable(functions, 'auth-resetEmailMFAForUser_v2');
        return await resetEmailMFAForUserFunction({ userId, tenantId: resolvedTenantId });
    },
);

const isSamlSsoEnabledForTenant = createAsyncThunk(
    'tenants/isSamlSsoEnabledForTenant',
    async (tenantId) => {
        if (!tenantId) return { isEnabled: false };
        const functions = getFunctions();
        const fn = httpsCallable(functions, 'saml-isSamlSsoEnabledForTenant_v2');
        const result = await fn({ tenantId });
        return result.data;
    },
);

const isEmailMfaEnabledForTenant = createAsyncThunk(
    'tenants/isEmailMfaEnabledForTenant',
    async (tenantIdArg, { getState }) => {
        const tenantId = tenantIdArg ?? getState()?.auth?.currentSelectedTenant;
        const functions = getFunctions();
        const isEmailMfaEnabledFn = httpsCallable(functions, 'auth-isEmailMfaEnabledForTenant_v2');
        const result = await isEmailMfaEnabledFn({ tenantId });
        return result.data;
    },
);

const signOutUser = createAsyncThunk(
    'tenants/signOutUser',
    async (_, { dispatch, getState }) => {
        const code = getState().core?.tenantCode;
        await signOut(getAuth());
        dispatch(tenantActions.logout());
        localStorage.removeItem('code');
        return code;
    },
);

const removeInvite = createAsyncThunk(
    'tenants/removeInvite',
    async (inviteId) => {
        const functions = getFunctions();
        const removeInviteByIdFunction = httpsCallable(functions, 'users-removeInviteById_v2');
        return await removeInviteByIdFunction(inviteId);
    },
);

const removeInviteByTenant = createAsyncThunk(
    'tenants/removeInviteByTenant',
    async (request) => {
        const functions = getFunctions();
        const removeInviteByIdFunction = httpsCallable(functions, 'users-removeInviteForTenantById_v2');
        return await removeInviteByIdFunction(request);
    },
);

// SAML Configuration Actions
const getSAMLConfig = createAsyncThunk(
    'tenants/getSAMLConfig',
    async (tenantId) => {
        const functions = getFunctions();
        const getSAMLConfigFunction = httpsCallable(functions, 'saml-getSAMLConfig_v2');
        return await getSAMLConfigFunction(tenantId);
    },
);

const updateSAMLConfig = createAsyncThunk(
    'tenants/updateSAMLConfig',
    async ({ tenantId, config }) => {
        const functions = getFunctions();
        const updateSAMLConfigFunction = httpsCallable(functions, 'saml-updateSAMLConfig_v2');
        return await updateSAMLConfigFunction({ tenantId, config });
    },
);

const testSAMLConfig = createAsyncThunk(
    'tenants/testSAMLConfig',
    async ({ tenantId, config }) => {
        const functions = getFunctions();
        const testSAMLConfigFunction = httpsCallable(functions, 'saml-testSAMLConfig_v2');
        return await testSAMLConfigFunction({ tenantId, config });
    },
);

// REDUCER SLICE --------------------------------

const { 
    getUpdatedList,
    trimActionType, 
    setAlertError,
    setAlertSuccess,
    alertInitialState,
    removeItemFromListById,
} = utils;

const initialState = {
    list: [],
    users: [],
    invites: [],
    selectedTenantId: null,
    selectedTenantUsers: [],
    selectedTenantInvites: [],
    selectedUserId: null,
    selectedUserDetail: null,
    selectedInviteId: null,
    rolesByTenantId: {},
    dealersByTenantId: {},
    facilitiesByTenantId: {},
    isLoading: {},
    isTenantStatusDialogOpen: false,
    isUserStatusDialogOpen: false,
    isDecommissionTenantDialogOpen: false,
    alert: alertInitialState,
    isEmailMfaEnabled: false,
    isSamlSsoEnabled: false,
};

export const TenantSlice = createSlice({
    name: 'tenants',
    initialState,
    reducers: {
        selectTenant(state, action) {
            state.selectedTenantId = action.payload;
        },
        selectUser(state, action) {
            state.selectedUserId = action.payload;
        },
        selectInvite(state, action) {
            state.selectedInviteId = action.payload;
        },
        toggleTenantStatusDialogOpen(state) {
            state.isTenantStatusDialogOpen = !state.isTenantStatusDialogOpen;
        },
        toggleDecommissionTenantDialogOpen(state) {
            state.isDecommissionTenantDialogOpen = !state.isDecommissionTenantDialogOpen;
        },
        toggleUserStatusDialogOpen(state) {
            state.isUserStatusDialogOpen = !state.isUserStatusDialogOpen;
        },
        hideAlert(state) {
            state.alert.open = false;
        },
        resetSlice() {
            return initialState;
        },
        logout() {
            //check in store.js to see state reset for logout
        },
    },
    extraReducers: {
        [inviteUser.fulfilled]: (state, action) => {
            state.selectedTenantInvites = [...state.selectedTenantInvites, action.payload.data];
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'User invited!');
        },
        [inviteUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [inviteUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [addTenant.fulfilled]: (state, action) => {
            state.list = [...state.list, action.payload.data];
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'New tenant created!');
        },
        [addTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [addTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [disableTenant.fulfilled]: (state, action) => {
            state.list = getUpdatedList(state.list, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The tenant has been disabled!');
        },
        [disableTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [disableTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enableTenant.fulfilled]: (state, action) => {
            state.list = getUpdatedList(state.list, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The tenant has been enabled!');
        },
        [enableTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [enableTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [decommissionTenant.fulfilled]: (state, action) => {
            state.list = removeItemFromListById(state.list, action.payload.data.id);
            state.isLoading[trimActionType(action.type)] = false;
            state.selectedTenantId = null;
            setAlertSuccess(state, 'The tenant has been decommissioned!');
        },
        [decommissionTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [decommissionTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [disableUser.fulfilled]: (state, action) => {
            state.selectedTenantUsers = getUpdatedList(state.selectedTenantUsers, action.payload.data);
            state.users = getUpdatedList(state.users, action.payload.data);
            state.selectedUserDetail = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The user has been disabled!');
        },
        [disableUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [disableUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [enableUser.fulfilled]: (state, action) => {
            state.selectedTenantUsers = getUpdatedList(state.selectedTenantUsers, action.payload.data);
            state.users = getUpdatedList(state.users, action.payload.data);
            state.selectedUserDetail = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The user has been enabled!');
        },
        [enableUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [enableUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getTenantRoleData.fulfilled]: (state, action) => {
            const { tenantId, roles, dealerGroups, dealerLocations, facilityGroups, facilityLocations } = action.payload.data;

            state.rolesByTenantId[tenantId] = roles;

            state.dealersByTenantId[tenantId] = {
                dealerGroups,
                dealerLocations,
            };

            if(facilityGroups && facilityLocations) {
                state.facilitiesByTenantId[tenantId] = {
                    facilityGroups,
                    facilityLocations,
                };
            }

            state.isLoading[trimActionType(action.type)] = false;
        },
        [getTenantRoleData.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getTenantRoleData.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getTenants.fulfilled]: (state, action) => {
            state.list = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getTenants.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getTenants.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getInvites.fulfilled]: (state, action) => {
            state.invites = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getInvites.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getInvites.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getInvitesByTenantId.fulfilled]: (state, action) => {
            state.selectedTenantInvites = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getInvitesByTenantId.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getInvitesByTenantId.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUserDetail.fulfilled]: (state, action) => {
            state.selectedUserDetail = action.payload.data;
            state.selectedTenantUsers = getUpdatedList(state.selectedTenantUsers, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUserDetail.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getUserDetail.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUsers.fulfilled]: (state, action) => {
            state.users = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUsers.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getUsers.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUsersByTenantId.fulfilled]: (state, action) => {
            state.selectedTenantUsers = action.payload.data;
            state.isLoading[trimActionType(action.type)] = false;
        },
        [getUsersByTenantId.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [getUsersByTenantId.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [updateTenant.fulfilled]: (state, action) => {
            state.list = getUpdatedList(state.list, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The tenant has been updated!');
        },
        [updateTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [updateTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [updateUser.fulfilled]: (state, action) => {
            state.users = getUpdatedList(state.users, action.payload.data);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'The user has been updated!');
        },
        [updateUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [updateUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
        },
        [removeInvite.fulfilled]: (state, action) => {
            state.invites = removeItemFromListById(state.invites, action.payload.data.inviteId);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Invite removed!');
        },
        [removeInvite.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [removeInvite.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [removeInviteByTenant.fulfilled]: (state, action) => {
            state.selectedTenantInvites = removeItemFromListById(state.selectedTenantInvites, action.payload.data.inviteId);
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Invite removed!');
        },
        [removeInviteByTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },        
        [removeInviteByTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [resetMFAForUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'MFA reset success!');
        },
        [resetMFAForUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [resetMFAForUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [resetPasskeyForUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'Passkey reset success!');
        },
        [resetPasskeyForUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [resetPasskeyForUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [resetTOTPForUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'TOTP reset success!');
        },
        [resetTOTPForUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [resetTOTPForUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [resetEmailMFAForUser.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertSuccess(state, 'EMAIL MFA reset success!');
        },
        [resetEmailMFAForUser.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [resetEmailMFAForUser.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            setAlertError(state, action.error);
        },
        [isSamlSsoEnabledForTenant.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            state.isSamlSsoEnabled = action.payload?.isEnabled ?? false;
        },
        [isSamlSsoEnabledForTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
            state.isSamlSsoEnabled = false;
        },
        [isSamlSsoEnabledForTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            state.isSamlSsoEnabled = false;
        },
        [isEmailMfaEnabledForTenant.fulfilled]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            state.isEmailMfaEnabled = action.payload?.isEmailMfaEnabled ?? false;
        },
        [isEmailMfaEnabledForTenant.pending]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = true;
        },
        [isEmailMfaEnabledForTenant.rejected]: (state, action) => {
            state.isLoading[trimActionType(action.type)] = false;
            state.isEmailMfaEnabled = false;
        },
    },
});

export const tenantActions = {
    ...TenantSlice.actions,
    addTenant,
    getTenants,
    updateTenant,
    disableTenant,
    enableTenant,
    decommissionTenant,
    disableUser,
    enableUser,
    getUsers,
    getUserDetail,
    getUsersByTenantId,
    getInvites,
    getInvitesByTenantId,
    getTenantRoleData,
    inviteUser,
    signOutUser,
    updateUser,
    removeInvite,
    removeInviteByTenant,
    resetMFAForUser,
    resetPasskeyForUser,
    resetTOTPForUser,
    getSAMLConfig,
    updateSAMLConfig,
    testSAMLConfig,
    resetEmailMFAForUser,
    isEmailMfaEnabledForTenant,
    isSamlSsoEnabledForTenant,
};

export default TenantSlice.reducer;
